import { formatDate, formatTime } from "shared-utils";
import { Addition, Deduction, PLInsuranceSkill, Strike, Timesheet, TurnedDownTimsheet, Payment } from "../types/Timesheets";
import { formatLocalTime } from "shared-utils";

export type JobTimesheetRow = {
  date: string;
  time?: string;
  hours?: number;
  crew_hours?: number;
  crew_adjusted_hours?: number;
  rowTotal: number;
  rowXpBonus?: number;
  cancelled?: boolean;
  cancelChargeNote?: string;
  // The level this shift was PAID at, when that is not the level they hold — i.e. they were
  // serving a demotion. Without it the figure is just lower, with nothing explaining why, and
  // the first they would know is a short payslip.
  demotedPayLevel?: string | null;
  additionDeductionNote?: string | null;
  type?: 'addition' | 'deduction' | 'strike' | 'plInsurance';
  jobCrewId?: number;
  timesheetIssueId?: number;
  outOfTownBonus?: number;
  drive?: number;
  driveBonus?: number;
  skills?: number;
  chief?: number;
  clientName?: string | null;
  venueName?: string | null;
  misc?: number;
}

export type JobTimesheetData = {
  rows: JobTimesheetRow[];
  total: number;
  nonTaxableAmount?: number;
  taxableAmount?: number;
  totalHours?: number;
}

type RateKeys = `rate${string}` | `london${string}`;

export const mapJobTimesheetToFrontend = (
  timesheet: Timesheet[],
  extraHoursRate: string | null,
  additions: Addition[],
  deductions: Deduction[],
  strikes: Strike[],
  plInsuranceRate: string | null,
  plInsuranceSkill: PLInsuranceSkill | null
): JobTimesheetData => {
  const rows: JobTimesheetRow[] = [];
  let total = 0;
  let driveTotal = 0;
  let nonTaxMiscTotal = 0;
  let totalHours = 0;
  timesheet.forEach((item) => {
    let cancelChargeNote = '';

    const effectiveHours = item.adjusted_hours ? Number(item.date_hours_needed) + Number(item.adjusted_hours || 0) : Number(item.date_hours_needed);

    // Use the base hours (date_hours_needed) for rate key lookup to ensure whole number
    const baseHours = Number(item.date_hours_needed);
    const rateKey: RateKeys = item.is_london_rate ? `london${baseHours}` : `rate${baseHours}`;
    const baseRate = isNaN(Number(item[rateKey as keyof Timesheet])) ? 0 : Number(item[rateKey as keyof Timesheet]);

    // Calculate hourly rate for fractional adjustments
    const hourlyRate = baseHours > 0 ? baseRate / baseHours : 0;

    // Apply adjustment to get final rate for effective hours
    const adjustmentHours = effectiveHours - baseHours;

    // 2 hours is the minimum to get a rate, then apply adjustment
    let rowTotal = (effectiveHours && effectiveHours >= 2)
      ? baseRate + (hourlyRate * adjustmentHours)
      : 0;

    let xpBonus = Number(item.crew_loyalty_bonus)

    if (item.date_day_cancelled) {
      // get cancel charge and note if job_date is cancelled
      if (item.date_day_cancelled_charge === 3) {
        cancelChargeNote = item.date_cancel_crew_note || ''
        rowTotal = Number(item.date_cancel_crew_amount || 0)
        xpBonus = 0
      } else {
        cancelChargeNote = `Date Cancelled - ${Number(item.date_crew_cancelled_charge) * 100}% Pay`;
        rowTotal = rowTotal * Number(item.date_crew_cancelled_charge)
        xpBonus = xpBonus * Number(item.date_crew_cancelled_charge)
      }
    }

    // add Misc to total if not cancelled or is cancelled & misc paid
    if (!item.date_day_cancelled || (item.date_day_cancelled && item)) {
      rowTotal += getCrewMiscTotal(item)
    }

    totalHours += effectiveHours + Number(item.hours);

    // add extra hours with NaN check like office app
    rowTotal += Number(item.hours) * (isNaN(Number(extraHoursRate)) ? 1 : Number(extraHoursRate))

    // Include xpBonus in rowTotal like office app
    rowTotal += xpBonus;

    rows.push({
      date: item.date_started_at ? formatDate(item.date_started_at, 'dd/MM/yyyy') : '',
      time: item.date_started_at ? formatLocalTime(item.date_started_at, "HH:mm") : undefined,
      hours: item.date_hours_needed || 0,
      crew_adjusted_hours: Number(item.adjusted_hours),
      crew_hours: Number(item.hours),
      rowTotal: rowTotal,
      cancelChargeNote,
      cancelled: item.date_day_cancelled || false,
      // item.name is the level the rates came from, which for a stamped shift IS the demoted
      // level. pay_level_id being set is what distinguishes a demotion from a real level change.
      demotedPayLevel: (item as any).pay_level_id ? ((item as any).name ?? null) : null,
      rowXpBonus: xpBonus || 0,
      outOfTownBonus: (item.gets_oot_bonus !== false && !(item as any).zero_travel_expense) ? Number(item.job_date_out_of_town_bonus_crew) : 0,
      drive: Number(item.drive),
      driveBonus: Number(item.drive_bonus),
      skills: Number(item.skills),
      chief: Number(item.chief),
      clientName: item.client_name,
      venueName: item.venue_name,
      jobCrewId: item.job_crew_id,
      timesheetIssueId: item.timesheet_issue_id || undefined,
      misc: Number(item.misc),
    });

    total += rowTotal;
    driveTotal += Number(item.drive);
    nonTaxMiscTotal += Number(item.non_tax_misc);
  });

  // add additions rows
  additions.forEach((addition) => {
    rows.push({
      date: formatDate(addition.date, 'dd/MM/yyyy'),
      time: '',
      rowTotal: Number(addition.amount),
      additionDeductionNote: addition.description,
      type: 'addition'
    });

    total += Number(addition.amount);
  });

  // add deductions rows
  deductions.forEach((deduction) => {
    rows.push({
      date: formatDate(deduction.date, 'dd/MM/yyyy'),
      time: '',
      rowTotal: -Number(deduction.amount),
      additionDeductionNote: deduction.description,
      type: 'deduction'
    });

    total -= Number(deduction.amount);
  });

  // add strike rows
  strikes.forEach((strike) => {
    rows.push({
      date: formatDate(strike.date, 'dd/MM/yyyy'),
      time: '',
      rowTotal: -Number(strike.amount),
      additionDeductionNote: `${strike.description} - ${strike.company}, ${strike.venue}`,
      type: 'strike'
    });

    total -= Number(strike.amount);
  })

  const parsedPLInsuranceRate = plInsuranceRate ? JSON.parse(plInsuranceRate) : null;
  const hasPlInsurance = plInsuranceSkill != null && (!plInsuranceSkill?.expires_at || plInsuranceSkill.expires_at > new Date());

  if (hasPlInsurance && parsedPLInsuranceRate && Array.isArray(parsedPLInsuranceRate)) {
    // Find the latest applicable rate
    const now = Date.now();
    const applicableRate = parsedPLInsuranceRate
      .filter(rate => rate.date * 1000 <= now)
      .sort((a, b) => b.date - a.date)[0];

    if (applicableRate) {
      rows.push({
        date: '',
        time: '',
        rowTotal: -Number(applicableRate.value) * totalHours,
        type: 'plInsurance',
      });

      total -= Number(applicableRate.value) * totalHours;
    }
  }

  const nonTaxableAmount = driveTotal + nonTaxMiscTotal;
  return {
    rows,
    total,
    nonTaxableAmount: nonTaxableAmount,
    taxableAmount: total - nonTaxableAmount,
    totalHours: totalHours
  };
};

export const mapTurnedDownToFrontend = (timesheet: TurnedDownTimsheet[], extraHoursRate: string | null, isFlatRate = false): JobTimesheetData => {
  const rows: JobTimesheetRow[] = [];
  let total = 0;

  timesheet.forEach((item: TurnedDownTimsheet) => {
    const rateKey: RateKeys = item.is_london_rate ? `london${item.date_hours_needed}` : `rate${item.date_hours_needed}`;
    let rowTotal = Number(item[rateKey as keyof TurnedDownTimsheet]);

    // add Misc to total
    rowTotal += getTurnedDownMiscTotal(item, isFlatRate);

    // add extra hours
    rowTotal += Number(item.turned_down_extra_hours) * Number(extraHoursRate)

    rows.push({
      date: item.date_started_at ? formatDate(item.date_started_at, 'dd/MM/yyyy') : '',
      time: item.date_started_at ? formatTime(item.date_started_at, 'HH:mm') : '',
      hours: item.turned_down_hours || 0,
      rowTotal: rowTotal,
      outOfTownBonus: Number(item.job_date_out_of_town_bonus_crew),
      drive: Number(item.turned_down_drive),
      driveBonus: Number(item.turned_down_drive_bonus),
      skills: isFlatRate ? 0 : Number(item.turned_down_skills),
      chief: isFlatRate ? 0 : Number(item.turned_down_chief),
      clientName: item.client_name,
      venueName: item.venue_name
    });

    total += rowTotal;
  });

  return {
    rows,
    total
  };
}

export const mapPaymentsToFrontend = (payments: Payment[]) => {
  return payments.map(payment => ({
    id: payment.id,
    date: payment.payment_date ? formatDate(payment.payment_date, 'dd/MM/yyyy') : '',
    description: payment.payment_note || 'Payment',
    amount: Number(payment.amount),
    status: payment.status,
    paymentFrom: payment.payment_from ? formatDate(payment.payment_from, 'dd/MM/yyyy') : null,
    paymentTo: payment.payment_to ? formatDate(payment.payment_to, 'dd/MM/yyyy') : null,
  }));
}

export type PaymentData = ReturnType<typeof mapPaymentsToFrontend>;


const getTurnedDownMiscTotal = (item: TurnedDownTimsheet, isFlatRate = false) => {
  return Number(item.job_date_out_of_town_bonus_crew) +
    Number(item.turned_down_drive) +
    Number(item.turned_down_drive_bonus) +
    (isFlatRate ? 0 : Number(item.turned_down_skills))
}


const getCrewMiscTotal = (item: Timesheet) => {
  // Only include out-of-town bonus if the level allows it (gets_oot_bonus is true) and zero_travel_expense is not set
  const ootBonus = (item.gets_oot_bonus !== false && !(item as any).zero_travel_expense) ? Number(item.job_date_out_of_town_bonus_crew) : 0;
  const isFlatRate = Boolean((item as any).is_flat_rate)

  return ootBonus +
    Number(item.drive) +
    Number(item.drive_bonus) +
    // Number(item.skills) + // Comment out skills like office app
    (isFlatRate ? 0 : Number(item.chief)) +
    Number(item.misc)
}